import {
  addMonths,
  differenceInMonths,
  isValid,
  addDays,
  subDays,
} from "date-fns";
import { getMonthsNumber } from "../../../utils/prices-calculator";

export const MINIMUM_PERIOD_MONTHS = 1;
export const MAXIMUM_PERIOD_MONTHS = 12;

export const MINIMUM_DAYS_FROM_DEPARTURE = 30;
export const MAXIMUM_DAYS_FROM_DEPARTURE = 366;

export const serializeDate = (date: Date): string => {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const deserializeDate = (dateString: string): Date => {
  if (!dateString) return new Date();
  
  const dateOnly = dateString.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
};

export const parseDateSafely = (dateString: string): Date | null => {
  if (!dateString || typeof dateString !== 'string') return null;
  
  const dateOnly = dateString.split('T')[0];
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return null;
  
  const [year, month, day] = dateOnly.split('-').map(Number);
  
  if (year < 1900 || year > 2200) return null;
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > 31) return null;
  
  const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  
  if (!isValid(date) || 
      date.getUTCFullYear() !== year || 
      date.getUTCMonth() !== month - 1 || 
      date.getUTCDate() !== day) {
    return null;
  }
  
  return date;
};

export const isDateAfter = (date1String: string, date2String: string): boolean => {
  const date1 = parseDateSafely(date1String);
  const date2 = parseDateSafely(date2String);
  
  if (!date1 || !date2) return false;
  
  return date1.getTime() > date2.getTime();
};

export const isDateBefore = (date1String: string, date2String: string): boolean => {
  const date1 = parseDateSafely(date1String);
  const date2 = parseDateSafely(date2String);
  
  if (!date1 || !date2) return false;
  
  return date1.getTime() < date2.getTime();
};

export const getTomorrowDate = (): string => {
  const tomorrow = new Date();
  tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
  tomorrow.setUTCHours(12, 0, 0, 0);
  return serializeDate(tomorrow);
};

export const getEighteenYearsAgoDate = (): string => {
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() - 18);
  date.setUTCHours(12, 0, 0, 0);
  return serializeDate(date);
};

export const getSixtyNineYears364DaysAgoDate = (): string => {
  const date = new Date();
  date.setUTCFullYear(date.getUTCFullYear() - 70);
  date.setUTCDate(date.getUTCDate() + 1);
  date.setUTCHours(12, 0, 0, 0);
  return serializeDate(date);
};

export const getMonthsDifference = (date1: string, date2: string): number => {
  const date1Obj = parseDateSafely(date1);
  const date2Obj = parseDateSafely(date2);
  
  if (!date1Obj || !date2Obj) return -1;
  
  return differenceInMonths(date1Obj, date2Obj);
};

export const getMaxReturnDate = (departureDate: string): string => {
  if (!departureDate.length) {
    const farFuture = new Date();
    farFuture.setUTCFullYear(farFuture.getUTCFullYear() + 10);
    farFuture.setUTCHours(12, 0, 0, 0);
    return serializeDate(farFuture);
  }
  
  const departureDateObj = parseDateSafely(departureDate);
  if (!departureDateObj) {
    const farFuture = new Date();
    farFuture.setUTCFullYear(farFuture.getUTCFullYear() + 10);
    farFuture.setUTCHours(12, 0, 0, 0);
    return serializeDate(farFuture);
  }
  
  const maxReturnDateObj = addDays(departureDateObj, MAXIMUM_DAYS_FROM_DEPARTURE);
  return serializeDate(maxReturnDateObj);
};

export const getMinReturnDate = (
  returnDate?: string,
  departureDate?: string
): string => {
  if (!departureDate || !departureDate.length) {
    return getTomorrowDate();
  }

  const departureDateObj = parseDateSafely(departureDate);
  if (!departureDateObj) {
    return getTomorrowDate();
  }

  const minReturnDateObj = addDays(departureDateObj, MINIMUM_DAYS_FROM_DEPARTURE - 1);
  return serializeDate(minReturnDateObj);
};

export const getMinDepartureDate = (
  returnDate: string,
  departureDate: string
): string => {
  const monthsDiff = getMonthsNumber(returnDate, departureDate);
  return monthsDiff < 0 ? getTomorrowDate() : departureDate;
};

export const getMinReturnDateN = (departureDate: any): string => {
  const departureDateObj = parseDateSafely(departureDate);
  if (!departureDateObj) {
    return getTomorrowDate();
  }
  
  const minReturnDateObj = subDays(
    addMonths(departureDateObj, MINIMUM_PERIOD_MONTHS), 
    1
  );
  return serializeDate(minReturnDateObj);
};

export const getMaxMonthsDate = (departureDate: any): string => {
  const departureDateObj = parseDateSafely(departureDate);
  if (!departureDateObj) {
    const farFuture = new Date();
    farFuture.setUTCFullYear(farFuture.getUTCFullYear() + 10);
    farFuture.setUTCHours(12, 0, 0, 0);
    return serializeDate(farFuture);
  }
  
  const maxMonthsDateObj = subDays(
    addMonths(departureDateObj, MAXIMUM_PERIOD_MONTHS), 
    1
  );
  return serializeDate(maxMonthsDateObj);
};

export const getMaxDepartureDate = (): string => {
  const farFuture = new Date();
  farFuture.setUTCFullYear(farFuture.getUTCFullYear() + 100);
  farFuture.setUTCHours(12, 0, 0, 0);
  return serializeDate(farFuture);
};
